import React, { useEffect, useState, Fragment } from "react";
import Card from "@mui/material/Card";
import EditRole from "./editRole";
import Loader from "components/Progress";
import DeleteAlert from "components/Alerts/Delete";
import Table from "components/Table/newTable";
import { AdminToolbar, AdminActions, RowAction, useBrowserTable, adminDateTime } from "components/Admin/adminTable";
import { useGetRolesQuery, useDeleteRoleMutation, rolesApi } from "services/roles";
import { useSelector, useDispatch } from "react-redux";
import { isSwitchTab } from "utils/sharingSwitches";
import CodeIcon from "@mui/icons-material/Code";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import GroupIcon from "@mui/icons-material/Group";
import SettingsIcon from "@mui/icons-material/Settings";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import "./index.css";

// The five Base-Permission module groups → an icon each, reused in the Modules
// cell to show at a glance which groups a role carries.
const utils = [
  { name: "Administration", icon: GroupIcon },
  { name: "Solution Features", icon: SettingsIcon },
  { name: "Analytics", icon: AnalyticsIcon },
  { name: "Solution Management", icon: CodeIcon },
  { name: "Solution Edit", icon: ImportExportIcon },
];
// the search box looks through these (the "Search by" picker can narrow it)
const searchFields = [
  { label: "Name", value: "name" },
  { label: "Description", value: "description" },
  { label: "Base Permissions", value: "moduleNames" },
];

export default function RM(props) {
  let token = window.localStorage.getItem("token");
  const [loader, setLoader] = useState(true);
  const metaData = useSelector((state) => state.metaData);
  const [selectedRole, setSelectedRole] = useState("");
  const [cloneRole, setCloneRole] = useState("");
  const [isCloneRole, setIsCloneRole] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addRole, setAddRole] = useState(false);
  const [roles, setRoles] = useState([]); // full list — never narrowed (callerRole is resolved from it)
  const getRoles = useGetRolesQuery(token);
  const [deleteRole, deleteResult] = useDeleteRoleMutation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [pickedFields, setPickedFields] = useState(searchFields.map((f) => f.value));

  function ifLoaded(state, component) {
    if (state) return <Loader top="20vh" />;
    else return component();
  }

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  useEffect(() => {
    if (!getRoles.isFetching && getRoles.isSuccess) {
      setRoles(getRoles.data?.payload || []);
      setLoader(false);
    } else if (!getRoles.isFetching && getRoles.isError) {
      // a failed read shows an empty table (not an endless spinner) and says why
      setLoader(false);
      showSnackbar("Roles", getRoles.error?.data?.message || "The roles could not be loaded", "error");
    }
  }, [getRoles.isFetching]);

  async function onDelete(e) {
    let deletedRole = await deleteRole({ token, id: e });
    if (deletedRole.data?.success) {
      setDeleteModal(false);
      showSnackbar("Roles", deletedRole.data?.message, "success");
    } else {
      showSnackbar("Roles", deletedRole.data?.message, "error");
    }
  }
  async function toggleDelete(id = null) {
    setActiveId(id);
    setDeleteModal((state) => !state);
  }

  function checkPermission() {
    let roleManagement = metaData.apps
      .find((app) => app.name == "Administration")
      ?.tabs.find((tab) => tab.name == "Role Management");
    if (roleManagement) {
      return roleManagement.permission == "ALL" ? true : false;
    }
  }

  const handleCloneRole = (rule) => {
    setCloneRole(rule);
    setSelectedRole(rule);
    setIsCloneRole(true);
  };

  const handleEditClose = () => {
    if (selectedRole) {
      setSelectedRole("");
      setCloneRole("");
      setIsCloneRole(false);
    }
    if (addRole) {
      setAddRole(false);
    }
  };

  // After a version conflict on save (solution sharing stage 6): fetch the role list again and
  // reopen the editor from the fresh row (the editor is keyed on the role's updatedAt, so it
  // remounts). A role that is gone, redacted or no longer editable closes the editor instead.
  const reloadSelectedRole = async () => {
    const id = selectedRole && selectedRole._id;
    const before = selectedRole && selectedRole.updatedAt;
    // A list refresh may already be in flight (the failed save invalidated the list); then the forced
    // fetch resolves with the older copy. Retry a few times until the row has changed (a conflict
    // means it did), so the editor never reopens on the copy it already had.
    let list = null;
    for (let attempt = 0; attempt < 6 && !list; attempt++) {
      if (attempt) await new Promise((resolve) => setTimeout(resolve, 700));
      const sub = dispatch(rolesApi.endpoints.getRoles.initiate(token, { forceRefetch: true }));
      const res = await sub;
      sub.unsubscribe();
      const payload = res && res.isSuccess && res.data && Array.isArray(res.data.payload) ? res.data.payload : null;
      const row = payload && payload.find((r) => String(r._id) === String(id));
      if (payload && (!row || row.updatedAt !== before)) list = payload;
    }
    if (!list) {
      showSnackbar("Roles", "The role could not be reloaded. Go back to the roles list and open it again.", "error");
      return;
    }
    const fresh = list.find((r) => String(r._id) === String(id));
    if (!fresh || fresh.redacted || !canAdminister(fresh)) {
      handleEditClose();
      showSnackbar("Roles", "This role is no longer one you can manage.", "info");
      return;
    }
    setRoles(list);
    setSelectedRole(fresh);
  };

  // A role is administrable (edit/clone/delete) if the caller is ⊤ or fully holds it
  // (editable); it is visible additionally if it is the caller's OWN role (view-only).
  const ownRoleId = () => String(window.localStorage.getItem("role"));
  // Administer (edit / delete / clone) requires STRICT domination: the caller can hold the
  // role AND it is NOT at the caller's own privilege level (elm.samePrivilege). Peers and the
  // caller's own role are view-only. The backend annotates editable + samePrivilege and
  // enforces the same peer rule on writes, so this is not merely cosmetic.
  const canAdminister = (elm) =>
    !!((metaData?.unscopedAdmin || elm.editable) && !elm.samePrivilege);
  const isVisible = (elm) =>
    metaData?.unscopedAdmin || elm.editable || String(elm._id) === ownRoleId();

  function columns() {
    const temp = [
      { id: "name", label: "Name", align: "center" },
      { id: "html3", label: "Description", align: "center", disableSorting: true },
      { id: "html2", label: "Base Permissions", align: "center", disableSorting: true },
      { id: "solutions", label: "Solutions", align: "center" },
      { id: "users", label: "Users", align: "center" },
      { id: "updated", label: "Updated", align: "center" },
    ];
    if (checkPermission())
      temp.push({ id: "html", label: "Actions", align: "center", disableSorting: true });
    return temp;
  }

  // A module row made up only of the sharing switches (Share / Receive Shared Solutions) does not
  // count as holding that module, in the icons or the search (solution sharing stage 6).
  const onlySwitches = (m) =>
    !!(m && (m.tabs || []).length && (m.tabs || []).every((t) => t && isSwitchTab(m.name, t.name)));

  // Modules cell: the five base-permission bubbles (present = branded, absent = grey).
  const html2 = (row) => {
    const elm = row.html;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "center" }}>
        {utils.map((util) => {
          // A Solution Management row holding only the sharing switches does not count as holding
          // Solution Management (solution sharing stage 6).
          const held = (elm.modules || []).some((m) => m.name == util.name && !onlySwitches(m));
          const Icon = util.icon;
          return (
            <div
              key={util.name}
              style={{
                padding: 8,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                backgroundColor: held ? metaData.branding.secondaryColor : "lightgrey",
              }}
            >
              <Tooltip title={util.name}>
                <Icon fontSize="small" sx={{ color: held ? "white" : "#777", fontSize: 16 }} />
              </Tooltip>
            </div>
          );
        })}
      </div>
    );
  };

  // Description cell: ellipsis with full-text tooltip.
  const html3 = (row) => (
    <Tooltip title={row.html.description || ""}>
      <div
        style={{
          color: "grey",
          maxWidth: 280,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {row.html.description}
      </div>
    </Tooltip>
  );

  // Actions cell: clone / edit-or-view / delete — same handlers + gating as the old cards.
  const html = (row) => {
    const elm = row.html;
    // Your own role and any role at your SAME privilege level (a peer) are view-only: no
    // edit, delete, or clone — only the eye. `admin` (strict domination) already excludes
    // peers + own via canAdminister; `self`/`peer` only refine the tooltip. The backend
    // enforces the same peer rule on writes.
    const self = String(elm._id) === ownRoleId();
    const peer = !!elm.samePrivilege;
    const admin = canAdminister(elm);
    const usersOnRole = typeof elm.userCount === "number" ? elm.userCount : 0;
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        {admin && (
          <RowAction title="Clone" ariaLabel="clone" icon={<FileCopyIcon />} onClick={() => handleCloneRole(elm)} />
        )}
        <RowAction
          title={
            admin
              ? "Edit"
              : self
              ? "View your role - the role assigned to you cannot be edited"
              : peer
              ? "View only - this role is at the same privilege level as yours"
              : "View"
          }
          ariaLabel={admin ? "edit" : "view"}
          icon={admin ? <EditIcon /> : <VisibilityIcon />}
          onClick={() => setSelectedRole(elm)}
        />
        {admin && (
          <RowAction
            title={
              usersOnRole > 0
                ? `Assigned to ${usersOnRole} user${usersOnRole > 1 ? "s" : ""} - reassign them before deleting`
                : "Delete"
            }
            ariaLabel="delete"
            icon={<DeleteIcon />}
            disabled={usersOnRole > 0}
            onClick={() => toggleDelete(elm._id)}
          />
        )}
      </div>
    );
  };

  // Visibility pre-filter applied to a DERIVED list; `roles` state stays full (callerRole in the
  // editor is resolved from the full list).
  const ownId = ownRoleId();
  const mapped = roles.filter(isVisible).map((elm) => {
    const self = String(elm._id) === ownId;
    return {
      _id: elm._id,
      // "(Self)" marks the caller's own assigned role. Kept inside the name string (not a JSX
      // chip) so it stays searchable and sortable.
      name: self ? `${elm.name || ""} (Self)` : elm.name || "",
      self,
      // Exclude the grant-all "*" sentinel row so a grant-all role's Solutions count reflects its
      // real solutions (not N+1).
      solutions: (elm.services || []).filter((s) => s && String(s.serviceId) !== "*").length,
      users: typeof elm.userCount === "number" ? elm.userCount : "",
      // shown in the shared date format ("-" when missing); sorted by time (updatedAt below)
      updated: adminDateTime(elm.updatedAt),
      // flat string search keys
      description: elm.description || "",
      moduleNames: (elm.modules || [])
        .filter((m) => !onlySwitches(m))
        .map((m) => m.name)
        .join(" "),
      html: elm, // full doc for the render props + row key
    };
  });
  // Pin the caller's own role to the top BY DEFAULT: with no column sorted the rows keep this
  // order; clicking a column header then sorts everything normally.
  const table = useBrowserTable({
    rows: [...mapped.filter((r) => r.self), ...mapped.filter((r) => !r.self)],
    search,
    fields: { name: (r) => r.name, description: (r) => r.description, moduleNames: (r) => r.moduleNames },
    picked: pickedFields,
    sortable: { name: (r) => r.name, solutions: (r) => r.solutions, users: (r) => r.users, updated: (r) => (r.html.updatedAt ? new Date(r.html.updatedAt).getTime() : null) },
  });

  function tableFunc() {
    return (
      <Table
        columns={columns()}
        rows={table.rows}
        html={html}
        html2={html2}
        html3={html3}
        page={table.page}
        totalPages={table.totalPages}
        totalDocuments={table.totalDocuments}
        handleChange={table.handleChange}
        pageSize={table.pageSize}
        setPageSize={table.setPageSize}
        pageSizeOptions={table.pageSizeOptions}
        sort={table.sort}
        setSort={table.setSort}
        height="calc(100vh - 280px)"
        minWidth="100%"
        overflowX="auto"
        // no side margins on the page-number row, or the table area scrolls sideways a second time
        paginationMargin="2vh 0"
      />
    );
  }

  return (
    <div>
      {selectedRole || cloneRole || addRole ? (
        <EditRole
          key={
            isCloneRole ? "clone" : selectedRole ? `${selectedRole._id}|${selectedRole.updatedAt || ""}` : "new"
          }
          solutionSharing={getRoles.data?.solutionSharing === true}
          onReloadRole={reloadSelectedRole}
          handleEditClose={handleEditClose}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          cloneRole={cloneRole}
          isCloneRole={isCloneRole}
          callerRole={roles.find((r) => String(r._id) === ownRoleId())}
          existingRoleNames={roles.map((r) => r.name || "")}
        />
      ) : (
        <Fragment>
          {checkPermission() ? (
            <AdminActions actions={[{ name: "Add Role", icon: <AddIcon />, onClick: () => setAddRole(true) }]} />
          ) : null}
          <Card style={{ maxWidth: "calc(100vw - 90px)" }}>
            <AdminToolbar
              title="Roles"
              icon={<AssignmentIndIcon color="disabled" />}
              search={{
                value: search,
                onChange: setSearch,
                placeholder: "Search name, description, base permissions ...",
                fields: searchFields,
                picked: pickedFields,
                onPick: setPickedFields,
              }}
            />
            <div style={{ margin: "20px", minHeight: "50vh" }}>{ifLoaded(loader, tableFunc)}</div>
          </Card>
          {activeId ? (
            <DeleteAlert
              deleteModal={deleteModal}
              title={`Delete (${(roles.find((r) => String(r._id) === String(activeId)) || {}).name || "role"})`}
              question="Are you sure you want to delete this role?"
              adminStyle
              deleteResult={deleteResult}
              platformCheck={false}
              id={activeId}
              handleDelete={onDelete}
              handleClose={toggleDelete}
            />
          ) : null}
        </Fragment>
      )}
    </div>
  );
}
